import { useCallback, useRef, useState, useEffect } from "react";
import { useMst } from "../../../stores/main";
import { pixelToFrame } from "../utils/timelineHelpers";
import { clampSceneTrimEnd } from "./dragUtils";
import { TRIM_EXTEND_TYPE } from "../../../helpers/modTypeConsts";
import { motionAPI } from "@blings/blings-player/editor-tools";
import { useAnimationJsonEdit } from "./useAnimationJsonEdit";

interface DragState {
  isDragging: boolean;
  initialTrimEnd: number;
  previewTrimEnd: number;
}

const initialDragState: DragState = {
  isDragging: false,
  initialTrimEnd: 0,
  previewTrimEnd: 0,
};

export interface UseSceneTrimDragReturn {
  isDragging: boolean;
  previewTrimEnd: number | null;
  startDrag: (e: React.MouseEvent) => void;
}

/**
 * Hook for dragging the scene trim end handle.
 * Updates the in-memory scene JSON duration and clears any legacy trim mod.
 */
export function useSceneTrimDrag(
  compositionStart: number,
  totalFrames: number,
  currentTrimEnd: number | null
): UseSceneTrimDragReturn {
  const { timelineStore, modsStore, undoStore } = useMst();
  const { zoom, scrollOffset } = timelineStore;
  const { applyAnimationJsonEdit } = useAnimationJsonEdit();

  const [dragState, setDragState] = useState<DragState>(initialDragState);
  const dragStateRef = useRef(dragState);
  dragStateRef.current = dragState;
  const rafIdRef = useRef<number | null>(null);
  const tracksAreaRef = useRef<HTMLElement | null>(null);

  const applySceneDurationChange = useCallback(
    (trimEnd: number) => {
      const existingTrimMod = modsStore.mods?.find(
        (mod) => mod?.moddata?.type === TRIM_EXTEND_TYPE
      );
      const nextCompositionEnd = compositionStart + trimEnd;

      applyAnimationJsonEdit(
        (workingAnimation) => {
          const applied = motionAPI.apply.updateCompositionTiming(
            workingAnimation as any,
            { op: nextCompositionEnd }
          );

          return applied.changes.length ? applied : false;
        },
        { manageUndo: false }
      );

      if (existingTrimMod) {
        modsStore.removeMod(existingTrimMod);
      }
    },
    [applyAnimationJsonEdit, compositionStart, modsStore]
  );

  const startDrag = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Find the tracks area for coordinate calculation
      tracksAreaRef.current = (e.currentTarget as HTMLElement).closest(
        ".timeline-tracks-area"
      ) as HTMLElement;

      undoStore.onGestureStart();

      // Use current trim end or totalFrames as initial value (relative to compositionStart)
      const initialTrimEndRelative = currentTrimEnd ?? totalFrames;
      // Convert to absolute frame for preview
      const initialTrimEndAbsolute = compositionStart + initialTrimEndRelative;

      setDragState({
        isDragging: true,
        initialTrimEnd: initialTrimEndRelative,
        previewTrimEnd: initialTrimEndAbsolute,
      });

      document.body.classList.add("timeline-edge-dragging");
    },
    [compositionStart, currentTrimEnd, totalFrames, undoStore]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const ds = dragStateRef.current;
      if (!ds.isDragging || !tracksAreaRef.current) return;

      // Cancel any pending RAF to avoid duplicate updates
      if (rafIdRef.current !== null) return;

      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;

        const rect = tracksAreaRef.current!.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const frame =
          pixelToFrame(mouseX, zoom, scrollOffset) + compositionStart;
        let clampedFrame = Math.round(frame);

        // Apply bounds checking: minimum duration from start, maximum extension limit
        clampedFrame = clampSceneTrimEnd(
          clampedFrame,
          compositionStart,
          totalFrames
        );

        setDragState((prev) => ({ ...prev, previewTrimEnd: clampedFrame }));
      });
    },
    [zoom, scrollOffset, compositionStart, totalFrames]
  );

  const handleMouseUp = useCallback(() => {
    const ds = dragStateRef.current;
    if (!ds.isDragging) return;

    // Cancel any pending RAF
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    // Apply the scene duration change to the in-memory JSON
    const trimEndFrame = ds.previewTrimEnd - compositionStart;
    applySceneDurationChange(trimEndFrame);

    undoStore.onGestureEnd();

    setDragState(initialDragState);
    document.body.classList.remove("timeline-edge-dragging");
  }, [applySceneDurationChange, compositionStart, undoStore]);

  // Document-level event listeners
  useEffect(() => {
    if (!dragState.isDragging) return;

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.classList.remove("timeline-edge-dragging");
    };
  }, [dragState.isDragging, handleMouseMove, handleMouseUp]);

  return {
    isDragging: dragState.isDragging,
    previewTrimEnd: dragState.isDragging ? dragState.previewTrimEnd : null,
    startDrag,
  };
}
